import {globalUser} from "./globals.js";
import {addTournamentMatches, getTournamentWinnerSuperstar, Tournament} from "../models/tournament.js";
import {addEventMatches, addEventTournaments, Event, getEventMatches} from "../models/event.js";
import {
    getActiveCompetitorsForCompany,
    getCompanyLatestEvent,
    getCompanyRosterByType,
    getCpuControlledCompanies,
    getRandomStaffMemberByRole
} from "./companies.js";
import {
    captureError,
    commaSeparate, getArrayMinByProperty,
    getChunks,
    getCurrentGame,
    getRandomChoice,
    getRandomInt,
    getRandomSample,
    getRosterMemberAge,
    getRosterMemberPopularity,
    getTalentCompany,
    myround,
    objectInArray,
    romanizeNumber,
    weightedChoice
} from "./helpers.js";
import {
    addSuperstarGraphicTemporaryTrophy,
    checkAvailability,
    getGraphicForSuperstar,
    getRosterMemberSuperstars,
    getSuperstarChampionshipsAll,
    updateSuperstarKayfabePoints,
    updateSuperstarPopularity
} from "./superstars.js";
import {CompanyLog} from "../models/companyLog.js";
import {
    getGameActiveCompanies,
    getGameCurrentUserCompany,
    getGameMainUserCompany,
    getUserControlledCompanies
} from "./games.js";
import {Companies, CompanyLogs, db, Events, Matches, Rosters, Superstars, Tournaments} from "../models/models.js";
import {
    addCompanyCompanyLogs,
    addCompanyEvents,
    addCompanyMessages,
    addCompanyNewsItems,
    addCompanyTournaments,
    getCompanyChampionships,
    getCompanyEvents,
    getCompanyStaff,
    getCompanyTournaments, removeCompanyEvents, removeCompanyTournaments
} from "../models/company.js";
import {
    createAnnualTournamentIndieWinnerAvailableMessage,
    createAnnualTournamentNoCompetitorsMessage,
    createAnnualTournamentOwnerWinMessage,
    createAnnualTournamentPresidentLossMessage,
    createAnnualTournamentPresidentNoParticipationMessage,
    createAnnualTournamentPresidentWinMessage,
    createAnnualTournamentWinnerMessage,
    createInvitationAcceptedMessage,
    createInvitationDeclinedMessage
} from "./messages.js";
import {getAnnualTournamentDialogs, otherDialogs, presidentsData} from "../data/annualTournaments.js";
import {
    addRosterSuperstars,
    addRosterTagTeams,
    getRosterCompany,
    getRosterSuperstars,
    getRosterTagTeams
} from "../models/roster.js";
import {addMatchParticipants, getMatchParticipants, Match} from "../models/match.js";
import {getSuperstarRoster} from "../models/superstar.js";
import {transferTalentToRoster} from "./rosters.js";
import {formatCompanyNameBackend} from "../ui/ui-helpers.js";
import {generateNewsItem} from "./news.js";
import {generateJournalistData, getEventPosterGraphicData, getEventPreviewMatchText} from "./events.js";
import {getAnnualTournamentWinLossPercentagesWeights, simulateAnnualTournamentMatchWinner} from "./matches.js";
import {processPlayMatch} from "./processMatch.js";
import {endEvent} from "./endEvent.js";
import {generateSuperstar, generateTagTeam, generateTrio} from "./generate.js";
import {addWinnerToNextRound, getNextRoundMatches} from "./tournaments.js";

async function getAnnualTournamentData(annualTournament, company, mainCompany, game) {  // get_annual_tournament_data
    let data = {};
    data['name'] = annualTournament.name;
    data['status'] = annualTournament.status;
    let companyData = getAnnualTournamentCompanyData(annualTournament, company);

    // Get dialog
    if (['selecting', 'running'].includes(annualTournament.status) ||
        (annualTournament.status === 'inviting' && !companyData)) {
        data['dialogData'] = await getAnnualTournamentDialogData(annualTournament, company, mainCompany, game, companyData)
    }

    return data;
}

function getAnnualTournamentCompanyData(annualTournament, company) {
    return annualTournament.companiesData[company.$loki.toString()];
}

async function getAnnualTournamentDialogData(annualTournament, company, mainCompany, game, companyData) {  // get_annual_tournament_dialog_data
    let data = {};
    let presidentData = getPresidentData(game);
    let participatingCompanies = await getParticipatingCompanies(annualTournament, game);
    let participatingCompaniesNames = commaSeparate(participatingCompanies.map(x => x.name));
    let event = await getCompanyLatestEvent(mainCompany);
    let previousTournaments = (await getCompanyTournaments(mainCompany))
        .filter(x => x.annualTournament && x.status === 'ended');
    let participationPoints = null;
    let entriesSample = null;
    let injuredParticipant = null;
    let bribeCost = null;

    if (annualTournament.status === 'inviting') {
        participationPoints = getAnnualTournamentParticipationPoints(company);
    }
    if (annualTournament.status === 'selecting') {
        entriesSample = await getAnnualTournamentEntriesSample(annualTournament, company);
        bribeCost = getAnnualTournamentBribeCost(previousTournaments);
    }
    if (annualTournament.status === 'running') {
        injuredParticipant = getAnnualTournamentInjuredParticipant(annualTournament);
    }

    data['dialogs'] = await getAnnualTournamentDialogs(annualTournament, companyData, event, previousTournaments,
        presidentData['name'], participatingCompaniesNames, entriesSample, participationPoints, injuredParticipant,
        bribeCost);
    data['information'] = presidentData;

    return data;
}

function addAnnualTournamentInjuredParticipantId(annualTournament, injuredParticipantId) {  // add_annual_tournament_injured_participant_id
    for(let [_, v] of Object.entries(annualTournament.companiesData)) {
        if(v['participantIds'] && v['participantIds'].includes(injuredParticipantId)) {
            v['injuredParticipantId'] = injuredParticipantId;
            break;
        }
    }
    return true;
}

function getAnnualTournamentInjuredParticipant(annualTournament) {  // get_annual_tournament_injured_participant
    let injuredParticipant = null;
    let injuredParticipantId = null;
    for (let companyData of Object.values(annualTournament.companiesData)) {
        if (companyData['injuredParticipantId']) {
            injuredParticipantId = companyData['injuredParticipantId'];
            break;
        }
    }
    if (injuredParticipantId) {
        injuredParticipant = Superstars.get(injuredParticipantId);
    }
    return injuredParticipant;
}

function getAnnualTournamentParticipationPoints(company) {
    return company.reputation * 50;
}

function getAnnualTournamentBribeCost(previousTournaments) {  // get_annual_tournament_bribe_cost
    return 300 + previousTournaments.length * 50;
}

async function getAnnualTournamentEntriesSample(annualTournament, company) {  // get_annual_tournament_entries_sample
    // Get all participants ids
    let allParticipantIds = getAnnualTournamentAllParticipantIds(annualTournament);

    // Get sample ids, don't include company superstars
    let randomInstance = new Math.seedrandom(annualTournament.meta.created);
    let mainRoster = await getCompanyRosterByType(company, 'main');
    let companySuperstarIds = (await getRosterSuperstars(mainRoster)).map(x => x.$loki);
    let competitorSuperstarIds = allParticipantIds.filter(id => !companySuperstarIds.includes(id));
    let sampleSize = 3;
    if (competitorSuperstarIds.length < sampleSize) {
        sampleSize = competitorSuperstarIds.length;
    }
    let participantIdsSample = getRandomSample(competitorSuperstarIds, sampleSize, randomInstance);

    return Promise.all(participantIdsSample.map(x => Superstars.get(x)));
}

async function processAnnualTournament(game, mainCompany, user, weeklyShow, eventsToSimulate = null) {  // process_annual_tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');
    const simulatingPastTournament = eventsToSimulate && eventsToSimulate !== 1;

    // Create tournament
    if (!annualTournament && !mainCompany.storyName && globalUser.paid && !weeklyShow &&
        game.settings['run_annual_tournament'] !== false && (await checkToAddAnnualTournament(game, mainCompany))) {

        // Check if competitors present
        const allCompetitors = await getActiveCompetitorsForCompany(mainCompany, game);
        if (allCompetitors.length) {

            // Get participating cpu companies
            const cpuCompanies = await getCpuControlledCompanies(mainCompany, game);
            const userCompanies = await getUserControlledCompanies(game);
            const [participatingCpuCompanies, participatingUserCompanies] = selectParticipatingCompanies(cpuCompanies,
                userCompanies);
            let notParticipatingUserCompanies = userCompanies.filter(x => !objectInArray(x, participatingUserCompanies));

            // Create tournament
            await createAnnualTournament(mainCompany, participatingCpuCompanies, notParticipatingUserCompanies);
        }
        // Add 'no competitors' message
        else if (game.monthCount === 4) {
            const presidentData = getPresidentData(game);
            const newMessage = await createAnnualTournamentNoCompetitorsMessage(presidentData['name'], game.monthCount, game.year);
            await addCompanyMessages(mainCompany, [newMessage]);
        }
    }

    // Process tournament
    else if (annualTournament) {

        // Change to 'waiting' after invitations processed
        if (annualTournament.status === 'inviting') {
            annualTournament.status = 'waiting';

            // Add user response if simulating todo test simulating, below too, test sim past TGI
            if (eventsToSimulate) {
                const response = getCompanyParticipationResponse(mainCompany);
                updateAnnualTournamentCompanyStatus(annualTournament, mainCompany, response);
            }
        }

        // Change to 'selecting' after monthly show
        if (annualTournament.status === 'waiting' && !weeklyShow) {
            // Create tournament events
            await createAnnualTournamentEvents(annualTournament, mainCompany);

            // Add number of entries
            const participatingCompanies = await getParticipatingCompanies(annualTournament, game);
            const numberOfEntriesData = getNumberOfEntriesData(participatingCompanies);
            addAnnualTournamentNumberOfEntriesData(annualTournament, numberOfEntriesData);

            // Select entries, select for user if simulating past TGI
            let organizations = participatingCompanies.filter(x => !x.userControlled);
            if (simulatingPastTournament) {
                organizations = [...participatingCompanies];
            }
            await selectParticipantsForOrganizations(annualTournament, numberOfEntriesData, organizations, mainCompany);

            annualTournament.status = 'selecting';

            // Simulate tournament if no user companies participating or simulating past TGI
            if (!participatingCompanies.some(x => x.userControlled) || simulatingPastTournament) {
                await startAnnualTournament(annualTournament, game, mainCompany);
                await simulateAnnualTournament(annualTournament, game, user, mainCompany);
            }
        }

        // End tournament if winner added
        if (annualTournament.status === 'running' && annualTournament.winnerName) {
            annualTournament.status = 'ended';
            await processAnnualTournamentEnd(annualTournament, game, mainCompany);
        }
    }

    return true;
}

async function checkToAddAnnualTournament(game, mainCompany) {  // check_to_add_annual_tournament
    let check = false;
    let currentMonth = game.monthCount;
    let annualTournamentMonth = 4;
    let previousTournaments = (await getCompanyTournaments(mainCompany)).filter(x => x.annualTournament && x.status === 'ended');
    // For existing saves past annual tournament's month, add if annual tournament is more than 3 months away
    if (currentMonth === annualTournamentMonth || (!previousTournaments.length &&
        (currentMonth > annualTournamentMonth || (currentMonth < 2 && game.year > 0)))) {
        check = true;
    }
    return check;
}

async function simulateAnnualTournament(annualTournament, game, user, mainCompany) {  // simulate_annual_tournament
    let events = (await getCompanyEvents(mainCompany)).reverse();
    let mainRoster = await getCompanyRosterByType(mainCompany, 'main');
    let rosterSuperstars = await getRosterSuperstars(mainRoster);

    try {
        // Go through Night 1 & 2, process matches and end events
        for (let event of [events[0], events[1]]) {
            let eventMatches = await getEventMatches(event);
            let sortedEventMatches = eventMatches.sort((a, b) => b.order - a.order);

            // Add poster data
            if (event === events[0]) {
                let eventPosterData = await getEventPosterGraphicData(event, eventMatches, [], null, annualTournament);
                if (eventPosterData) {
                    event.posterGraphic = eventPosterData['graphics'];
                }
            } else {
                event.posterGraphic = events[0].posterGraphic;
            }

            // Play matches
            for (let match of sortedEventMatches) {
                let winner = await simulateAnnualTournamentMatchWinner(match, annualTournament);
                await processPlayMatch(match, winner.name, event, mainCompany, mainCompany, mainRoster,
                    null, game, {}, {}, null, rosterSuperstars, null, null, null, null, null, true);
            }

            // End event
            await endEvent(mainCompany, user, true, rosterSuperstars);
        }
    }
    catch (err) {
        // If error simulating TGI, remove events and tournament
        captureError(err);
        await removeCompanyEvents(mainCompany, [events[0], events[1]]);
        await removeCompanyTournaments(mainCompany, [annualTournament]);
    }

    return true;
}

async function processAnnualTournamentEnd(annualTournament, game, mainCompany) {  // process_annual_tournament_end
    let month = game.monthCount;
    let year = game.year;
    let annualTournamentWinner = await getTournamentWinnerSuperstar(annualTournament);
    let annualTournamentWinnerCompany = await getTalentCompany(annualTournamentWinner);
    let presidentData = getPresidentData(game);
    let allCompanies = await getGameActiveCompanies(game);
    let participatingCompanies = await getParticipatingCompanies(annualTournament, game);
    let userControlledParticipatingCompanies = participatingCompanies.filter(x => x.userControlled);

    // Send message from president
    if (userControlledParticipatingCompanies.length > 0) {
        let newMessage;
        if (annualTournamentWinnerCompany && annualTournamentWinnerCompany.userControlled) {
            // Send win message to user
            newMessage = await createAnnualTournamentPresidentWinMessage(presidentData['name'], month, year);
            await addCompanyMessages(annualTournamentWinnerCompany, [newMessage]);
        } else {
            // Send loss message to user if CPU/Indies won
            newMessage = await createAnnualTournamentPresidentLossMessage(presidentData['name'], month, year);
            await addCompanyMessages(userControlledParticipatingCompanies[0], [newMessage]);
        }
    } else {
        // Send message to user if they didn't participate
        let newMessage = await createAnnualTournamentPresidentNoParticipationMessage(presidentData['name'],
            annualTournamentWinner, month, year);
        await addCompanyMessages(mainCompany, [newMessage]);
    }

    if (annualTournamentWinnerCompany) {
        if (annualTournamentWinnerCompany.userControlled) {
            // Add creative points bonus and message from owner
            let pointsBonus = myround(getRandomInt(200, 300), 50);
            annualTournamentWinnerCompany.points += pointsBonus;
            let newMessage = await createAnnualTournamentOwnerWinMessage(annualTournamentWinner, pointsBonus,
                annualTournamentWinnerCompany, annualTournament, month, year);
            await addCompanyMessages(annualTournamentWinnerCompany, [newMessage]);

            // Add message from winner
            newMessage = await createAnnualTournamentWinnerMessage(annualTournamentWinner, annualTournament, month, year);
            await addCompanyMessages(annualTournamentWinnerCompany, [newMessage]);
        }

        // Add fans to winning company
        if (game.marketSize) {
            let fanIncrease = game.marketSize * 0.05;
            if (fanIncrease > 300000) {
                fanIncrease = 300000;
            }
            annualTournamentWinnerCompany.fans += fanIncrease;

            // Read just fans based on market size
            let totalFans = allCompanies.reduce((total, company) => total + company.fans, 0);
            let multiplyValue = game.marketSize / totalFans;
            for (const x of allCompanies) {
                x.fans = myround(x.fans * multiplyValue, 100);
            }
        } else {
            annualTournamentWinnerCompany.fans += 20000;
        }

        await addAnnualTournamentWinCompanyLog(annualTournamentWinnerCompany, annualTournamentWinner, annualTournament,
            game, mainCompany);
    } else {
        // Move indie winner to available indies if not already there
        let indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
        if ((await getSuperstarRoster(annualTournamentWinner))?.$loki === indieRoster.$loki) {
            let indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
            let transferred = await transferTalentToRoster(annualTournamentWinner, indieAvailableRoster);
            for (let x of transferred) {
                x.availableThisMonth = true;
            }
        }

        // Add message from scout
        let scout = getRandomStaffMemberByRole((await getCompanyStaff(mainCompany)), 'scout');
        let newMessage = await createAnnualTournamentIndieWinnerAvailableMessage(annualTournamentWinner, month, year, scout);
        await addCompanyMessages(mainCompany, [newMessage]);
    }

    // Add headline news for winner
    let superstarCompanyName = getAnnualTournamentParticipantOrganizationName(annualTournamentWinner, annualTournament,
        allCompanies);
    let newsItem = await generateNewsItem({
        company: mainCompany, month, year, newsType: 'annual_tournament_won', annualTournamentName: annualTournament.name,
        superstarCompanyName: superstarCompanyName, superstar: annualTournamentWinner, headline: true
    });
    await addCompanyNewsItems(mainCompany, [newsItem]);

    // Add kayfabe points for winner
    updateSuperstarKayfabePoints(annualTournamentWinner, 100);

    // Add trophy graphic to winner
    if (annualTournamentWinner.graphic) {
        annualTournamentWinner.graphic = addSuperstarGraphicTemporaryTrophy(annualTournamentWinner.graphic)
    }

    // Set all user companies as 'playing'
    if (game.settings['control_multiple_companies']) {
        let userControlledCompanies = await getUserControlledCompanies(game);
        for (let x of userControlledCompanies) {
            x.turnStatus = 'playing';
        }
    }

    return true;
}

async function addAnnualTournamentWinCompanyLog(company, winner, annualTournament, game, mainCompany) {  // add_annual_tournament_win_company_log
    let newLog = CompanyLog(game.monthCount, mainCompany.realYear);
    await CompanyLogs.insert(newLog);
    newLog.text = `${annualTournament.name} (${winner.name})`;
    newLog.type = 'award';
    newLog.realYear = game.year;
    newLog.value = 3;
    await addCompanyCompanyLogs(company, [newLog]);
    return newLog;
}

function getAnnualTournamentParticipantOrganizationName(participant, tournament, allCompanies) {  // get_annual_tournament_participant_organization_name
    const organization = getAnnualTournamentParticipantOrganization(participant, tournament, allCompanies);
    if (organization) {
        return formatCompanyNameBackend(organization.name);
    }
    else {
        return 'Indies'
    }
}

function getAnnualTournamentParticipantOrganization(participant, tournament, allCompanies) {  // get_annual_tournament_participant_organization
    let organizationKey;
    for (const key in tournament.companiesData) {
        if (tournament.companiesData.hasOwnProperty(key)) {
            const participantIds = tournament.companiesData[key]['participantIds'] || [];
            if (participantIds.indexOf(participant.$loki) >= 0) {
                organizationKey = key;
                break;
            }
        }
    }
    return allCompanies.find(x => x.$loki.toString() === organizationKey)
}

function getPresidentData(game) {  // get_president_data
    const gameRandom = new Math.seedrandom(game.meta.created);
    return getRandomChoice(presidentsData, gameRandom());
}

async function createAnnualTournament(mainCompany, participatingCpuCompanies, notParticipatingUserCompanies) {  // create_annual_tournament
    const tournamentName = await getAnnualTournamentName(mainCompany);
    let annualTournament = Tournament(tournamentName, 'elimination', 4);
    await Tournaments.insert(annualTournament);
    annualTournament.annualTournament = true;
    annualTournament.prestige = 'A';
    annualTournament.xp = 300;
    annualTournament.status = 'inviting';
    annualTournament.companiesData = {};
    for (let x of participatingCpuCompanies) {
        annualTournament.companiesData[x.$loki.toString()] = {status: 'accepted'};
    }
    for (let x of notParticipatingUserCompanies) {
        annualTournament.companiesData[x.$loki.toString()] = {status: 'declined'};
    }
    annualTournament.companiesData['Indies'] = {status: 'accepted'};
    await addCompanyTournaments(mainCompany, [annualTournament]);
    return annualTournament;
}

async function getAnnualTournamentName(mainCompany) {  // get_annual_tournament_name
    let previousAnnualTournaments = [];
    for (let x of (await getCompanyTournaments(mainCompany))) {
        if (x.annualTournament && x.status === 'ended') {
            previousAnnualTournaments.push(x);
        }
    }
    let name = 'The Grand Invitational';

    // Add iteration number
    if (previousAnnualTournaments.length) {
        let iteration = previousAnnualTournaments.length + 1;
        // Convert to roman numeral if 30 or below
        if (iteration <= 30) {
            iteration = romanizeNumber(iteration);
        }
        name = `${name} ${iteration}`;
    }
    return name;
}

function selectParticipatingCompanies(cpuCompanies, userCompanies) {  // select_participating_cpu_companies
    // Everyone accepts by default
    let participatingCpuCompanies = [...cpuCompanies];
    let participatingUserCompanies = [...userCompanies];

    // Remove companies if plentiful
    if (participatingCpuCompanies.length > 2) {
        for (let competitor of participatingCpuCompanies) {
            const response = getCompanyParticipationResponse(competitor);
            if (response === 'declined') {
                // Remove from participating companies
                participatingCpuCompanies = participatingCpuCompanies.filter(x => x.$loki !== competitor.$loki);
                if (participatingCpuCompanies.length <= 2) {  // Don't let participating companies get low
                    break;
                }
            }
        }
    }

    // Remove lowest xp companies if total above 15 (leaves one space for indies)
    // Remove cpu before user
    while (participatingCpuCompanies.length + participatingUserCompanies.length > 15) {
        let lowestXpCompetitor = getArrayMinByProperty(participatingCpuCompanies, 'xp');
        if (lowestXpCompetitor) {
            participatingCpuCompanies = participatingCpuCompanies.filter(x => x.$loki !== lowestXpCompetitor.$loki);
        }
        else {
            let lowestXpUserCompany = getArrayMinByProperty(participatingUserCompanies, 'xp');
            participatingUserCompanies = participatingUserCompanies.filter(x => x.$loki !== lowestXpUserCompany.$loki);
        }
    }

    return [participatingCpuCompanies, participatingUserCompanies];
}

function getCompanyParticipationResponse(company) {  // get_company_participation_response
    let response = 'accepted';

    // Higher chance for high or low REP companies to decline
    let chanceToDecline = 1;
    if (company.reputation <= 2 || company.reputation >= 9) {
        chanceToDecline = 5;
    }

    // Get chance to decline
    if (getRandomInt(1, 100) <= chanceToDecline) {
        response = 'declined';
    }

    return response;
}

function updateAnnualTournamentCompanyStatus(annualTournament, company, status) {  // update_annual_tournament_company_status
    let companyData = getAnnualTournamentCompanyData(annualTournament, company);
    if (companyData === undefined) {
        // Add status to companies data
        annualTournament.companiesData[company.$loki.toString()] = {status: status};
    }
    return true;
}

async function createAnnualTournamentEvents(annualTournament, mainCompany) {  // create_annual_tournament_events
    // Create events
    let events = [
        Event(`${annualTournament.name} Night 2`),
        Event(`${annualTournament.name} Night 1`)
    ];
    for (let x of events) {
        await Events.insert(x);
        x.prestige = 'A';
        x.temporaryEvent = true;
        x.year = mainCompany.realYear;
    }
    await addCompanyEvents(mainCompany, events);
    await addEventTournaments(events[1], [annualTournament]);
    return true;
}

async function getParticipatingCompanies(annualTournament, game) {  // get_participating_companies
    let participatingCompanies = [];
    for (let x of (await getGameActiveCompanies(game))) {
        // Get companies with "accepted" status
        let companyData = getAnnualTournamentCompanyData(annualTournament, x);
        if (companyData && companyData['status'] === "accepted") {
            participatingCompanies.push(x);
        }
    }
    return participatingCompanies;
}

function getNumberOfEntriesData(participatingCompanies) {  // get_number_of_entries_data
    let data = {};
    let totalParticipants = 16;
    let maximumIndies = getMaximumIndies(totalParticipants, participatingCompanies);
    let averageParticipants = getAverageParticipants(participatingCompanies, totalParticipants);
    let sortedCompanies = participatingCompanies.sort((a, b) => (b.reputation - a.reputation) || (b.fans - a.fans));

    // Add participants for each company
    for (let company of sortedCompanies) {
        data[company.$loki] = getNumberOfCompanyParticipants(company, sortedCompanies, averageParticipants);
    }

    // Add remaining participants
    data = addRemainingParticipantsToIndiesData(data, totalParticipants, maximumIndies, sortedCompanies);

    return data;
}

function getMaximumIndies(totalParticipants, participatingCompanies) {  // get_maximum_indies
    let maximumIndies = Math.floor(totalParticipants / 4);
    if (participatingCompanies.length >= 5) {
        maximumIndies -= 1;
    }
    if (participatingCompanies.length >= 10) {
        maximumIndies -= 1;
    }
    return maximumIndies;
}

function getAverageParticipants(participatingCompanies, totalParticipants) {  // get_average_participants
    let totalOrganizations = participatingCompanies.length + 1; // Add indies
    let averageParticipants = Math.floor(totalParticipants / totalOrganizations);
    if (averageParticipants < 1) {
        averageParticipants = 1;
    }
    return averageParticipants;
}

function getNumberOfCompanyParticipants(company, sortedCompanies, averageParticipants) {  // get_number_of_company_participants
    // Use average participants as a baseline of number of participants to add
    let reputationRatio = company.reputation / sortedCompanies[0].reputation;  // highest company reputation
    return Math.ceil(averageParticipants * reputationRatio);
}

function addRemainingParticipantsToIndiesData(data, totalParticipants, maximumIndies, sortedCompanies) {  // add_remaining_participants_to_indies_data
    // Get remaining participants
    let remainingParticipants = totalParticipants - Object.values(data).reduce((a, b) => a + b, 0);

    // If participants are more than maximum indies, add to companies
    while (remainingParticipants > maximumIndies && sortedCompanies.length) {
        for (let company of sortedCompanies) {
            data[company.$loki] += 1;
            remainingParticipants -= 1;
            if (remainingParticipants <= maximumIndies) {
                break;
            }
        }
    }

    // Add remaining participants to indies
    data['Indies'] = remainingParticipants;

    return data;
}

function getAnnualTournamentCompanyNumberOfEntries(annualTournament, company) {  // get_annual_tournament_company_number_of_entries
    let numberOfEntries = null;
    let companyData = getAnnualTournamentCompanyData(annualTournament, company)
    if (companyData) {
        numberOfEntries = companyData['numberOfEntries'];
    }
    return numberOfEntries;
}

function addAnnualTournamentNumberOfEntriesData(annualTournament, numberOfEntriesData) {  // add_annual_tournament_number_of_entries_data
    for (let [k, v] of Object.entries(numberOfEntriesData)) {
        if (annualTournament.companiesData[k.toString()]) {
            annualTournament.companiesData[k.toString()]['numberOfEntries'] = v;
        }
    }
    return numberOfEntriesData;
}

async function selectParticipantsForOrganizations(annualTournament, numberOfEntriesData, companies, mainCompany,
                                                  participantType = null) {  // select_participants_for_organizations
    let organizations = [...companies, 'Indies']; // Competitors and Indies
    let indieRosters = [(await getCompanyRosterByType(mainCompany, 'indie')),
        (await getCompanyRosterByType(mainCompany, 'indieAvailable'))];

    for (let organization of organizations) {
        // Get number of entries
        let organizationKey = (organization === 'Indies') ? organization : organization.$loki;
        let numberOfEntries = numberOfEntriesData[organizationKey];

        // Select participants for organization
        let participants = await selectOrganizationParticipants(organization, numberOfEntries, indieRosters, participantType);
        let participantIds = participants.map(x => x.$loki);

        // Add to companies data
        addAnnualTournamentCompanyParticipants(annualTournament, organization, participantIds);
    }

    return true;
}

function addAnnualTournamentCompanyParticipants(annualTournament, company, participantIds) {  // add_annual_tournament_company_participants
    let companyKey = company === 'Indies' ? company : company.$loki.toString();
    if (annualTournament.companiesData[companyKey]) {
        annualTournament.companiesData[companyKey]['participantIds'] = participantIds;
    }
    return true;
}

async function selectOrganizationParticipants(organization, numberOfEntries, indieRosters, participantType) {  // select_organization_participants
    // Get weights
    let rosters = (organization === 'Indies') ? indieRosters : [await getCompanyRosterByType(organization, 'main')];
    let talent = await getAvailableTalent(rosters, participantType);
    let participantsWeights = await getParticipantsWeights(talent);

    // Select participants
    return selectParticipantsFromWeights(participantsWeights, numberOfEntries);
}

async function getAvailableTalent(rosters, participantType) {  // get_available_talent
    // Get talent based on participant type
    let rosterTalent;
    switch (participantType) {
        case 'tag':
            rosterTalent = (await Promise.all(rosters.map(x => getRosterTagTeams(x)))).flat().filter(x => !x.trio && !x.temporary);
            break;
        case 'trio':
            rosterTalent = (await Promise.all(rosters.map(x => getRosterTagTeams(x)))).flat().filter(x => x.trio && !x.temporary);
            break;
        default:
            rosterTalent = (await Promise.all(rosters.map(x => getRosterSuperstars(x)))).flat();
    }

    // Get available talent
    let availableTalent = [];
    for (const x of rosterTalent) {
        let ts = await getRosterMemberSuperstars(x);
        if (ts.every(y => checkAvailability(y, false))) {
            availableTalent.push(x);
        }
    }

    // Restrict female talent if enough male talent
    availableTalent = await filterFemaleTalent(availableTalent);

    return availableTalent;
}

async function filterFemaleTalent(talent) {  // filter_female_talent
    let maleTalent = [];
    for (const x of talent) {
        let ts = await getRosterMemberSuperstars(x);
        if (ts.every(y => y.gender === 'male')) {
            maleTalent.push(x);
        }
    }
    if (maleTalent.length >= 10) {
        talent = maleTalent;
    }
    return talent;
}

async function getParticipantsWeights(participants) {  // get_participants_weights
    let weights = [];

    // Add weights from top and young participants
    let topParticipants = await getTopParticipants(participants);
    let youngParticipants = await getYoungParticipants(participants, topParticipants);

    // Get weights for participants
    for (let participant of [...topParticipants, ...youngParticipants]) {
        let weight = await getParticipantWeight(participant);
        weights.push([participant, weight]);
    }

    return weights;
}

async function getParticipantWeight(participant) {  // get_participant_weight
    let popularity = await getRosterMemberPopularity(participant);
    if (popularity > 1000) {
        popularity = 1000;
    }

    // Get weight based on popularity
    return (popularity / 100) ** 2 / 100;
}

async function getTopParticipants(participants) {  // get_top_participants
    let sortedPopularityParticipants = await getSortedParticipantsByPopularity(participants);
    return sortedPopularityParticipants.slice(0, 15);
}

async function getYoungParticipants(participants, topParticipants) {  // get_young_participants
    let sorted = [...participants].filter(p => !objectInArray(p, topParticipants));
    for (const x of sorted) {
        x.tempAge = await getRosterMemberAge(x);
    }
    sorted = sorted.sort((a, b) => a.tempAge - b.tempAge);
    for (const x of sorted) {
        delete x.tempAge;
    }
    return sorted.slice(0, 3);
}

async function getSortedParticipantsByPopularity(participants) {  // get_sorted_participants_by_popularity
    let sorted = [...participants];
    for (const x of sorted) {
        x.tempPopularity = await getRosterMemberPopularity(x);
    }
    sorted = sorted.sort((a, b) => b.tempPopularity - a.tempPopularity)
    for (const x of sorted) {
        delete x.tempPopularity;
    }
    return sorted;
}

function selectParticipantsFromWeights(weights, numberOfEntries) {  // select_participants_from_weights
    let participants = [];
    for (let x = 0; x < numberOfEntries; x++) {
        if (weights.length) {
            let selection = weightedChoice(weights);
            weights = weights.filter(y => y[0].$loki !== selection.$loki);
            participants.push(selection);
        }
    }
    return participants;
}

async function startAnnualTournament(annualTournament, game, mainCompany) {  // start_annual_tournament
    // Get participants
    let participants = await getAllParticipantsForTournament(annualTournament, game, mainCompany);

    // Create matches, add to tournament events
    let events = (await getCompanyEvents(mainCompany)).reverse();
    await createAnnualTournamentMatches(annualTournament, events, participants, game, mainCompany);
    annualTournament.status = 'running';

    // Set all user companies besides game as 'ended', set game as current company
    if (game.settings['control_multiple_companies']) {
        game.currentCompanyId = null;
        let userControlledCompanies = await getUserControlledCompanies(game);
        for (let x of userControlledCompanies) {
            if (x.$loki !== mainCompany.$loki) {
                x.turnStatus = 'ended';
            }
        }
    }

    return true;
}

async function getAllParticipantsForTournament(annualTournament, game, mainCompany, participantType = null) {  // get_all_participants_for_tournament

    // Get participants from companies data
    let allParticipantIds = getAnnualTournamentAllParticipantIds(annualTournament);
    let allParticipants = await Superstars.find({'$loki': {'$in': allParticipantIds}});

    // Generate indie participants if not enough participants
    let totalParticipants = Object.values(annualTournament.companiesData).map(y => y['numberOfEntries'] || 0)
        .reduce((a, b) => a + b, 0);
    if (allParticipants.length < totalParticipants) {
        let indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
        let generatedParticipants = await generateIndieParticipants(totalParticipants, indieRoster, participantType,
            allParticipants);
        allParticipants.push(...generatedParticipants);

        // Add generated participant ids to 'Indies' in companies data
        annualTournament.companiesData['Indies']['participantIds'].push(...generatedParticipants.map(x => x.$loki));
    }

    return allParticipants;
}

async function generateIndieParticipants(totalParticipants, indieRoster, participantType, participants) {  // generate_indie_participants
    let generatedParticipants = [];
    let numberToGenerate = totalParticipants - participants.length;
    let gender = participants.length ? (await getRosterMemberSuperstars(participants[0]))[0].gender : 'male';

    if (participantType === 'tag') {
        numberToGenerate *= 2;
    } else if (participantType === 'trio') {
        numberToGenerate *= 3;
    }

    // Generate superstars
    let generatedSuperstars = [];
    for (let i = 0; i < numberToGenerate; i++) {
        let newSuperstar = await generateSuperstar({popularity: getRandomInt(400, 750), gender: gender, notManager: true});
        generatedSuperstars.push(newSuperstar);
        await addRosterSuperstars(indieRoster, [newSuperstar]);
    }

    // Generate tag teams
    if (participantType === 'tag' || participantType === 'trio') {
        let superstarsPerTeam = participantType === 'tag' ? 2 : 3;
        let superstarsData = chunk(generatedSuperstars, superstarsPerTeam);
        for (let x of superstarsData) {
            let newTeam;
            if (participantType === 'tag') {
                newTeam = await generateTagTeam(x);
            } else {
                newTeam = await generateTrio(x);
            }
            generatedParticipants.push(newTeam);
            await addRosterTagTeams(indieRoster, [newTeam]);
        }
    } else {
        generatedParticipants = generatedSuperstars;
    }

    return generatedParticipants;
}

function getAnnualTournamentAllParticipantIds(annualTournament) {  // Previously get_annual_tournament_all_participant_ids
    let allParticipantIds = [];
    for (const x of Object.values(annualTournament.companiesData)) {
        const companyParticipantIds = x['participantIds'];
        if (companyParticipantIds) {
            allParticipantIds.push(...companyParticipantIds)
        }
    }
    return allParticipantIds
}

async function createAnnualTournamentMatches(annualTournament, events, participants, game, mainCompany) {  // create_annual_tournament_matches
    const tournamentRounds = 4;

    // Get first round matches from seeds
    let seeds = await getTournamentSeeds(participants, game, mainCompany, annualTournament);
    let firstRoundMatchGroups = [...getMatchesFromSeeds(seeds)].reverse();

    // Add future matches
    let futureMatches = await addFutureMatchesToEvent(tournamentRounds, firstRoundMatchGroups, events[1]);

    // Add first round matches
    let firstRoundMatches = await addMatchesToEvent(firstRoundMatchGroups, events[0]);

    // Add matches to tournament
    await addTournamentMatches(annualTournament, futureMatches.concat(firstRoundMatches));

    return true;
}

async function getTournamentSeeds(participants, game, mainCompany, annualTournament) {  // get_tournament_seeds
    // Get nested array of participants sorted by popularity then into organization groups e.g [[a, b], [c, d]]
    let organizationGroups = await getOrganizationGroups(participants, game, mainCompany, annualTournament);

    // Add seeds by cycling through organization groups until they're filled
    let seeds = [];
    while (seeds.length < participants.length) {
        for (const organizationGroup of organizationGroups) {
            if (organizationGroup.length) {
                let seed = seeds.length + 1;
                let participant = organizationGroup[0];
                seeds.push([participant, seed]);
                organizationGroup.shift();
                participant.tournamentSeed = seed;
            }

            // End when seeds are full
            if (seeds.length >= participants.length) {
                break;
            }
        }
    }

    return seeds;
}

async function getOrganizationGroups(participants, game, mainCompany, annualTournament) {  // get_organization_groups / get_participants_for_organizations
    let allCompanies = await getGameActiveCompanies(game);

    // Sort organizations by top first, indies at end
    let organizations = [...(await getParticipatingCompanies(annualTournament, game)), 'Indies'];
    organizations.sort((a, b) => (b.reputation - a.reputation) || (b.fans - a.fans));

    // Group participants, sorted by popularity, by organization into dict e.g {organization: [participants]}
    let participantsForOrganizations = {};
    let sortedParticipants = await getSortedParticipantsByPopularity(participants);
    for (let participant of sortedParticipants) {
        let participantOrganization = getAnnualTournamentParticipantOrganization(participant, annualTournament,
            allCompanies);
        if (!participantOrganization) {
            participantOrganization = 'Indies'
        }
        let key = participantOrganization === 'Indies' ? participantOrganization : participantOrganization.$loki;
        if (participantsForOrganizations[key]) {
            participantsForOrganizations[key].push(participant);
        } else {
            participantsForOrganizations[key] = [participant];
        }
    }

    // Get participants for sorted organizations in arrays
    return organizations.map(x => {
        let key = x === 'Indies' ? x : x.$loki;
        return participantsForOrganizations[key] || [];
    });
}

function getMatchesFromSeeds(seeds) {  // get_matches_from_seeds
    let matches = [];
    let nextNodes = [seeds[0], seeds[1]];
    let rounds = Math.floor(Math.log2(seeds.length));

    for (let z = 0; z < rounds - 1; z++) {
        let nodes = [...nextNodes];
        nextNodes = [];

        // Add seed to previous round
        for (let x of nodes) {
            nextNodes.push(...[x, null]);
        }

        let nextSeeds = seeds.slice(nextNodes.length / 2, nextNodes.length);
        for (let x of nextSeeds) {
            // Find seed to pair with
            let i = nextNodes.length + 1 - x[1];
            // Add seed to node after i
            let node = nextNodes.find(y => y && y[1] === i);
            let pos = nextNodes.indexOf(node) + 1;
            nextNodes[pos] = x;
        }
    }

    // Reverse second half
    nextNodes = nextNodes.splice(0, nextNodes.length / 2).concat(nextNodes.reverse());

    // Divide nodes into two superstars
    for (const x of getChunks(nextNodes, 2)) {
        let participants = [x[1][0], x[0][0]];
        matches.push(participants);
    }

    return matches;
}

async function addFutureMatchesToEvent(tournamentRounds, firstRoundMatchGroups, event) {  // add_future_matches_to_event
    // Add blank matches for final rounds
    let addedMatches = [];
    for (let tournamentRound = tournamentRounds; tournamentRound > 1; tournamentRound--) {
        let matchesToAdd = firstRoundMatchGroups.length * 2 / (2 ** tournamentRound);
        for (let y = 0; y < matchesToAdd; y++) {
            let addedMatch = await addMatchToEvent([], event, tournamentRound);
            addedMatches.push(addedMatch);
        }
    }
    return addedMatches;
}

async function addMatchesToEvent(firstRoundMatchGroups, event) {  // add_matches_to_event
    // Add first round matches
    let addedMatches = [];
    for (let matchParticipants of firstRoundMatchGroups) {
        let addedMatch = await addMatchToEvent(matchParticipants, event, 1);
        addedMatches.push(addedMatch);
    }
    return addedMatches;
}

async function addMatchToEvent(participants, event, tournamentRound) {  // add_match_to_event
    let participantType = getParticipantType(participants);
    const newMatch = Match(event.matchesCount, participantType === 'tag' ? 'tag' : 'singles');
    await Matches.insert(newMatch);
    await addEventMatches(event, [newMatch]);
    event.matchesCount += 1;
    await addMatchParticipants(newMatch, participants);
    newMatch.tournamentRound = tournamentRound;
    return newMatch;
}

function getParticipantType(participants) {  // get_participant_type
    let participantType = null;
    if (participants.length && participants[0].objectName === 'tagTeam') {
        participantType = 'tag';
    }
    return participantType;
}

async function getAnnualTournamentMatchPredictions(eventMatches, eventName, annualTournament) {  // get_annual_tournament_match_predictions
    const data = {};
    const predictions = [];
    const winners = [];
    const allMatches = [...eventMatches].sort((a, b) => a.order - b.order);

    for (const match of allMatches) {
        const participants = await getMatchParticipants(match);

        // Don't run for matches without participants e.g tournament finals
        if (!participants.length) {
            continue;
        }

        // Get predicted winner
        const weights = await getAnnualTournamentWinLossPercentagesWeights(participants);
        const winner = weightedChoice(weights);
        winners.push(winner);

        // Get win text
        const predictionText = `${winner.name}.`;
        predictions.push({
            matchText: await getEventPreviewMatchText(match, false, true),
            predictionText: predictionText
        });
    }

    // Get tournament winner prediction on Night 2
    if (eventName.endsWith('2')) {
        const tournamentWinner = await getAnnualTournamentWinnerPrediction(winners);
        const predictionText = `${tournamentWinner.name}.`;
        predictions.push({
            matchText: `${annualTournament.name} Winner`,
            predictionText: predictionText
        });
    }

    data['predictions'] = predictions;

    // Get journalist data
    const journalistData = generateJournalistData();
    data['journalistName'] = journalistData.name;
    data['journalistPossessivePronoun'] = journalistData.possessivePronoun;

    return data;
}

async function getAnnualTournamentWinnerPrediction(participants) {  // get_annual_tournament_winner_prediction
    const weights = await getParticipantsWeights(participants);
    return weightedChoice(weights);
}

async function getAnnualTournamentPreviewTexts(event, eventMatches, annualTournament, mainCompany, game) {  // get_annual_tournament_preview_texts
    const previewTexts = [];
    const participatingCompanies = await getParticipatingCompanies(annualTournament, game);
    const participatingCompaniesNames = commaSeparate(participatingCompanies.map(x => x.name).concat('the Indies'));

    // Get top matches by popularity of participants
    let sortedMatches = [...eventMatches];
    for (const x of sortedMatches) {
        let p = await getMatchParticipants(x);
        if (p.length) {
            let totalPopularity = await Promise.all(p.map(s => getRosterMemberPopularity(s)));
            x.tempPopularity = totalPopularity.reduce((a, b) => a + b, 0);
        }
    }
    sortedMatches = sortedMatches.filter(x => x.tempPopularity !== undefined).sort((a, b) => b.tempPopularity - a.tempPopularity);
    for (const x of sortedMatches) {
        delete x.tempPopularity;
    }

    // Night 1
    if (event.name.endsWith('1')) {
        const previousTournament = (await getCompanyTournaments(mainCompany)).reverse()
            .find(x => x.annualTournament && x.status === 'ended');
        const allParticipantIds = getAnnualTournamentAllParticipantIds(annualTournament);

        // Intro text
        if (previousTournament) {
            previewTexts.push(
                'Fans have been eagerly anticipating the tournament, and it starts tonight in front of a sold out crowd!'
            );
        } else {
            previewTexts.push(
                'Fans have been dreaming about this tournament ever since it was announced, and it finally starts tonight!'
            );
        }

        // Companies text
        previewTexts.push(
            `Talent from <span class="font-bold">${participatingCompaniesNames}</span> are competing to see who takes home the trophy.`
        );

        // Previous winner text
        if (previousTournament) {
            const winner = await getTournamentWinnerSuperstar(previousTournament);
            if (winner) {
                if (allParticipantIds.includes(winner.$loki)) {
                    previewTexts.push(
                        `<span class="font-bold">${winner.name}</span>, winner of the last tournament, is participating ` +
                        `and hoping to win again.`
                    );
                } else {
                    previewTexts.push(
                        `<span class="font-bold">${winner.name}</span>, who won the last tournament, is sadly not ` +
                        `participating this time.`
                    );
                }
            }
        }

        // Entries text
        previewTexts.push('Here is the complete entry list:');
        const allParticipants = await Promise.all(allParticipantIds.map(x => Superstars.get(x)));
        const entriesTexts = allParticipants.sort((a, b) => a.tournamentSeed - b.tournamentSeed).map((p, i) => {
            const participantOrganization = getAnnualTournamentParticipantOrganizationName(p, annualTournament,
                participatingCompanies);
            const classHtml = i > 0 ? ' class="margin-top-5"' : '';
            return `<div${classHtml}>- ${p.name} (${participantOrganization})</div>`;
        });
        previewTexts.push(entriesTexts.join(''));

        // Matches text
        const topMatchesPreviewText = commaSeparate(
            await Promise.all(sortedMatches.slice(0, 3).map(x => getEventPreviewMatchText(x)))
        );
        previewTexts.push(`Some first round matches we're excited for include ${topMatchesPreviewText}.`);

        // Continue text
        previewTexts.push(`Who will move on to Night 2 of ${annualTournament.name}? Continue for our predictions.`);
    }
    // Night 2
    else if (event.name.endsWith('2')) {
        // Intro text
        previewTexts.push('Excitement is high for Night 2 of the tournament, but so is the tension. The winner will be crowned tonight!');

        // Remaining participants text
        let topRemainingParticipants = (await Promise.all(sortedMatches.map(x => getMatchParticipants(x)))).flat();
        topRemainingParticipants = topRemainingParticipants.sort((a, b) => a.tournamentSeed - b.tournamentSeed).slice(0, 4);
        const topRemainingParticipantsData = topRemainingParticipants.map(p => {
            const participantOrganization = getAnnualTournamentParticipantOrganizationName(p, annualTournament,
                participatingCompanies);
            return `${p.name} (${participantOrganization})`;
        });
        previewTexts.push(`Top stars that progressed to Night 2 include ` +
            `<span class="font-bold">${commaSeparate(topRemainingParticipantsData)}</span>.`);

        // Matches text
        const topMatchesPreviewText = commaSeparate(
            await Promise.all(sortedMatches.slice(0, 2).map(x => getEventPreviewMatchText(x)))
        );
        previewTexts.push(`Fans cannot wait for the quarter final matchups, especially ${topMatchesPreviewText}.`);

        // Continue text
        previewTexts.push(`Who will go on to win ${annualTournament.name}? Continue for our predictions.`);
    }

    return previewTexts;
}

function getAnnualTournamentOpeningSegmentData(game, annualTournament) {  // get_annual_tournament_opening_segment_data
    const presidentData = getPresidentData(game);
    return {
        name: "Opening Segment",
        startTexts: [`The show opens with the president, ${presidentData.name}, standing in the middle of the ring ...`],
        actionTexts: [`${presidentData.name} welcomes the fans to ${annualTournament.name} and promises an amazing show!`],
        graphics: [presidentData.graphic],
        segmentType: "segment",
        annualTournament: true
    };
}

function getAnnualTournamentWinnerSegmentData(game, annualTournament, winner) {  // get_annual_tournament_winner_segment_data
    const presidentData = getPresidentData(game);

    // Get attire or graphic of winner if present, otherwise get empty graphic
    let winnerGraphic = getGraphicForSuperstar(winner);
    delete winnerGraphic['uid'];

    return {
        name: "Trophy Presentation",
        startTexts: [`${presidentData['name']} comes down to the ring and announces ${winner.name} as the winner of ${annualTournament.name}.`],
        actionTexts: [`${winner.name} lifts the trophy as fireworks go off! ${presidentData['name']} thanks everyone and asks them to come again next time.`],
        graphics: [winnerGraphic, presidentData['graphic']],
        segmentType: "segment",
        annualTournament: true
    };
}

async function updateAnnualTournamentCompanyStatusView({action} = {}) {  // update_annual_tournament_company_status_view
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);

    // Get annual tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');
    if (!annualTournament) {
        data['error'] = 'tournament-not-found';
        return data;
    }

    // Change user company's status
    let status = (action === 'acceptInvitation') ? 'accepted' : 'declined';
    updateAnnualTournamentCompanyStatus(annualTournament, userCompany, status);

    // Add message from owner
    let newMessage;
    if (status === 'accepted') {
        newMessage = await createInvitationAcceptedMessage(userCompany, game.monthCount, game.year);
    } else {
        newMessage = await createInvitationDeclinedMessage(userCompany, game.monthCount, game.year);
    }
    await addCompanyMessages(userCompany, [newMessage]);

    db.saveDatabase();

    return data;
}

async function getAnnualTournamentAvailableSelections({injuryReplacement} = {}) {  // get_annual_tournament_available_selections
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);

    // Get annual tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');

    // Get available superstars
    let company;
    if (injuryReplacement) {
        // Get injured superstar's company
        let companyKey = null;
        for (let [k, v] of Object.entries(annualTournament.companiesData)) {
            if (v['injuredParticipantId']) {
                companyKey = k;
                break;
            }
        }
        company = (companyKey === String(userCompany.$loki)) ? userCompany : Companies.get(parseInt(companyKey));
    } else {
        company = userCompany;
    }

    let mainRoster = await getCompanyRosterByType(company, 'main');
    let superstars = await getRosterSuperstars(mainRoster);
    let sortedSuperstars = [...superstars].sort((a, b) => b.popularity - a.popularity);
    let companyChampionships = await getCompanyChampionships(company);

    // Mark current participants as unavailable if tournament injury replacement
    let allParticipantIds = [];
    if (injuryReplacement && annualTournament) {
        allParticipantIds = getAnnualTournamentAllParticipantIds(annualTournament);
    }

    data['selections'] = sortedSuperstars.map(x => ({
        'name': x.name,
        'popularity': x.popularity,
        'uid': x.$loki,
        'appearances': (x.partTimer && !x.retired) ? x.appearances : null,
        'retired': x.retired,
        'injured': x.injured > 0 ? x.injured : false,
        'championships': getSuperstarChampionshipsAll(x, companyChampionships),
        'fullTimeManager': x.fullTimeManager,
        'available': checkAvailability(x, false) && !allParticipantIds.includes(x.$loki)
    }));

    // Get number of entries
    if (annualTournament) {
        if (injuryReplacement) {
            data['numberOfEntries'] = 1;
        } else {
            data['numberOfEntries'] = getAnnualTournamentCompanyNumberOfEntries(annualTournament, userCompany);
        }
    }

    return data;
}

async function selectAnnualTournamentCompanyParticipants({participantUids} = {}) {  // select_annual_tournament_entries_view
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);

    // Get annual tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');
    if (!annualTournament) {
        data['error'] = 'tournament-not-found';
        return data;
    }

    // Get participants
    let mainRoster = await getCompanyRosterByType(userCompany, 'main');
    let participants = (await getRosterSuperstars(mainRoster)).filter(x => participantUids.includes(x.$loki));

    // Check number of entries
    let numberOfEntries = getAnnualTournamentCompanyNumberOfEntries(annualTournament, userCompany);
    if (participants.length > numberOfEntries) {
        data['error'] = 'too-many-entries';
        return data;
    }

    // Add user company participants
    addAnnualTournamentCompanyParticipants(annualTournament, userCompany, participantUids);

    // Add popularity boosts to participants
    addAnnualTournamentParticipantsPopularityBoost(participants);

    // Add participation points
    let participationPoints = getAnnualTournamentParticipationPoints(userCompany);
    if (participants.length === numberOfEntries) {
        data['dialog'] = otherDialogs['entriesSelectedFull'];
    } else {
        participationPoints = myround(participationPoints * (participants.length / numberOfEntries), 50);
        data['dialog'] = otherDialogs['entriesSelectedNotFull'];
    }
    userCompany.points += participationPoints;

    db.saveDatabase();

    // Get data to return
    data['points'] = userCompany.points;

    return data;
}

function addAnnualTournamentParticipantsPopularityBoost(participants) {  // add_annual_tournament_participants_popularity_boost
    for (let participant of participants) {
        let boost = getRandomInt(20, 30);
        updateSuperstarPopularity(participant, boost);
    }
    return true;
}

async function addAnnualTournamentCompanyBribeView() {  // add_annual_tournament_company_bribe_view
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);

    // Get bribe cost
    let previousTournaments = (await getCompanyTournaments(mainCompany))
        .filter(x => x.annualTournament && x.status === 'ended');
    let bribeCost = getAnnualTournamentBribeCost(previousTournaments);

    // Get annual tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');
    if (!annualTournament) {
        data['error'] = 'tournament-not-found';
        return data;
    }

    if (userCompany.points >= bribeCost) {
        // Add user company bribe
        addAnnualTournamentCompanyBribe(annualTournament, userCompany);

        // Remove points
        userCompany.points -= bribeCost;
        db.saveDatabase();

        data['success'] = true;
        data['dialog'] = otherDialogs['bribeAccepted'];
        data['points'] = userCompany.points;
    }
    else {
        data['dialog'] = otherDialogs['bribeNotEnoughPoints'];
    }

    return data;
}

async function replaceAnnualTournamentCompanyInjuredParticipant({replacementUid} = {}) {  // replace_annual_tournament_company_injured_participant_view
    let data = {};
    let game = await getCurrentGame();
    let mainCompany = await getGameMainUserCompany(game);

    // Get latest event match
    let event = await getCompanyLatestEvent(mainCompany);
    let eventMatches = (await getEventMatches(event)).filter(x => x.winner !== null);
    let latestMatch = eventMatches.sort((a, b) => a.order - b.order)[0];

    // Get annual tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');
    if (!annualTournament) {
        data['error'] = 'tournament-not-found';
        return data;
    }

    // Get replacement
    let replacement = null;
    if (replacementUid) {
        replacement = Superstars.get(replacementUid)
    }

    // Get injured participant
    let injuredParticipant = getAnnualTournamentInjuredParticipant(annualTournament);

    // Replace injured participant
    let replacementId = null;
    if (replacement) {
        replacementId = replacement.$loki;
        data['dialog'] = [...otherDialogs['replacementAdded']];
        data['dialog'][0] = data['dialog'][0].format(injuredParticipant.name);
    }
    // If replacement not found, use previous round's winner
    else {
        let matchParticipants = await getMatchParticipants(latestMatch);
        replacement = matchParticipants.find(x => x.name !== latestMatch.winner);
        data['dialog'] = [...otherDialogs['replacementNotAdded']];
        data['dialog'][0] = data['dialog'][0].format(replacement.name);
    }
    addAnnualTournamentInjuryReplacement(annualTournament, injuredParticipant.$loki, replacementId);

    // Add replacement to next round
    let nextRoundMatches = await getNextRoundMatches(annualTournament, latestMatch.tournamentRound);
    await addWinnerToNextRound(nextRoundMatches, replacement, event, false, null, annualTournament);

    db.saveDatabase();

    // Get injury data
    data['tournamentInjury'] = {
        injured: injuredParticipant.name,
        participantGoingThrough: replacement.name,
        injuredPronoun: injuredParticipant.pronoun,
        tournamentName: annualTournament.name,
        type: 'elimination',
        replacement: true
    };

    return data;
}

async function getAnnualTournamentCpuReplacement(annualTournament, organization, mainCompany) {  // get_annual_tournament_cpu_replacement
    // Select a talent who is not already part of tournament
    let replacement = null;

    // Get weights
    const rosters = organization ? [await getCompanyRosterByType(organization, 'main')] :
        [await getCompanyRosterByType(mainCompany, 'indie'), await getCompanyRosterByType(mainCompany, 'indieAvailable')];
    const allParticipantIds = getAnnualTournamentAllParticipantIds(annualTournament);
    const talent = (await getAvailableTalent(rosters, null)).filter(x => !allParticipantIds.includes(x.$loki));
    const participantsWeights = await getParticipantsWeights(talent);

    // Select replacement
    if (participantsWeights.length) {
        replacement = weightedChoice(participantsWeights);
    }

    return replacement;
}

async function startAnnualTournamentView() {  // start_annual_tournament_view
    let data = {};
    let game = await getCurrentGame();
    let mainCompany = await getGameMainUserCompany(game);

    // Get annual tournament
    let annualTournament = (await getCompanyTournaments(mainCompany)).find(x => x.annualTournament && x.status !== 'ended');
    if (!annualTournament) {
        data['error'] = 'tournament-not-found';
        return data;
    }

    // Check if all companies who accepted have selected their participants
    for (let y of Object.values(annualTournament.companiesData)) {
        if (!y['participantIds'] && y['status'] === 'accepted' && y['numberOfEntries']) {
            data['dialog'] = otherDialogs['startTournamentIncompleteEntries'];
            return data;
        }
    }

    // Start annual tournament
    await startAnnualTournament(annualTournament, game, mainCompany);

    db.saveDatabase();

    // Get data to return
    data['dialog'] = otherDialogs['startTournament'];
    data['success'] = true;

    return data;
}

function addAnnualTournamentInjuryReplacement(annualTournament, injuredParticipantId, replacementId) {  // add_annual_tournament_injury_replacement
    for (let [_, v] of Object.entries(annualTournament.companiesData)) {
        if (v['participantIds'] && v['participantIds'].includes(injuredParticipantId)) {
            // Add replacementId
            if (replacementId) {
                v['participantIds'].push(replacementId);
            }

            // Remove injuredParticipantId
            delete v['injuredParticipantId'];
            break;
        }
    }

    return true;
}

function addAnnualTournamentCompanyBribe(annualTournament, company) {  // add_annual_tournament_company_bribe
    let companyData = getAnnualTournamentCompanyData(annualTournament, company);
    if (companyData) {
        companyData['bribed'] = true;
    }
    return true;
}

export {processAnnualTournament, getPresidentData, getAnnualTournamentAllParticipantIds, getAnnualTournamentPreviewTexts,
    getAnnualTournamentParticipantOrganizationName, getAnnualTournamentParticipantOrganization,
    getAnnualTournamentMatchPredictions, getAnnualTournamentOpeningSegmentData, getAnnualTournamentData,
    replaceAnnualTournamentCompanyInjuredParticipant, startAnnualTournamentView, addAnnualTournamentCompanyBribeView,
    selectAnnualTournamentCompanyParticipants, getAnnualTournamentAvailableSelections, updateAnnualTournamentCompanyStatusView,
    addAnnualTournamentInjuredParticipantId, getAnnualTournamentCpuReplacement, getAnnualTournamentDialogData,
    addAnnualTournamentInjuryReplacement, getAnnualTournamentWinnerSegmentData}
